import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Timer — v2 port of v1's timer.ejs.
 * Generic up/down timer. Optional event-driven time adds (raid → +seconds, tip → +seconds).
 *
 * Config (all optional):
 *   mode: 'down' | 'up'                            (default 'down')
 *   initialSeconds: number                         (default 600 for down, 0 for up)
 *   accentColor: string                            (default '#8b5cf6')
 *   title: string                                  (default '' — hidden when empty)
 *   compact: boolean                               (default false)
 *   warningSeconds: number (turn red below this)   (default 60)
 *   autoStart: boolean                             (default true)
 *   addOnRaid: number (seconds per raid)           (default 0 — disabled)
 *   addOnTipPer100Coins: number                    (default 0)
 *   addOnSub: number                               (default 0)
 *   onFinishMessage: string                        (default 'Time!')
 */

function fmt(seconds: number, showHours: boolean): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (showHours || h > 0) return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(m)}:${pad(sec)}`;
}

interface FloatPop {
  id: number;
  text: string;
}

let popCounter = 0;

export function TimerWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const mode = (fields.mode as 'down' | 'up' | undefined) ?? 'down';
  const initial =
    fields.initialSeconds !== undefined ? Number(fields.initialSeconds) : mode === 'down' ? 600 : 0;
  const accent = String(fields.accentColor ?? '#8b5cf6');
  const title = String(fields.title ?? '');
  const compact = (fields.compact as boolean | undefined) ?? false;
  const warningSeconds = Number(fields.warningSeconds ?? 60);
  const autoStart = (fields.autoStart as boolean | undefined) ?? true;
  const addOnRaid = Number(fields.addOnRaid ?? 0);
  const addOnTipPer100 = Number(fields.addOnTipPer100Coins ?? 0);
  const addOnSub = Number(fields.addOnSub ?? 0);
  const onFinishMessage = String(fields.onFinishMessage ?? 'Time!');

  const [seconds, setSeconds] = useState(initial);
  const [running, setRunning] = useState(autoStart);
  const [pops, setPops] = useState<FloatPop[]>([]);
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => {
      setSeconds((s) => {
        if (mode === 'down') return Math.max(0, s - 1);
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, mode]);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    lastId.current = latestEvent.id;
    let add = 0;
    if (latestEvent.type === 'raid' && addOnRaid > 0) add = addOnRaid;
    else if (latestEvent.type === 'tip' && addOnTipPer100 > 0)
      add = Math.round((latestEvent.coins / 100) * addOnTipPer100);
    else if (latestEvent.type === 'tip_received' && addOnTipPer100 > 0)
      add = Math.round((latestEvent.amountCents / 100) * addOnTipPer100);
    else if (latestEvent.type === 'sub' && addOnSub > 0) add = addOnSub;
    if (add > 0) {
      setSeconds((s) => s + add);
      const id = ++popCounter;
      const mins = Math.floor(add / 60);
      const secs = add % 60;
      const text = mins > 0 ? `+${mins}:${String(secs).padStart(2, '0')}` : `+${secs}s`;
      setPops((p) => [...p, { id, text }]);
      setTimeout(() => setPops((p) => p.filter((x) => x.id !== id)), 1500);
    }
  }, [latestEvent, addOnRaid, addOnTipPer100, addOnSub]);

  const finished = mode === 'down' && seconds === 0;
  const warning = mode === 'down' && seconds > 0 && seconds < warningSeconds;
  const showHours = seconds >= 3600 || initial >= 3600;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(15,12,30,0.92), rgba(22,16,46,0.92))',
        border: `1px solid ${warning ? '#ef4444' : accent}66`,
        borderRadius: 14,
        padding: compact ? '8px 14px' : '12px 20px',
        position: 'relative',
        overflow: 'visible',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxShadow: warning ? '0 0 24px rgba(239,68,68,0.4)' : undefined,
        animation: warning ? 't-warn 1s ease-in-out infinite' : undefined,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 24,
          right: 24,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${warning ? '#ef4444' : accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      {title && !compact && (
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.5)',
            marginBottom: 4,
          }}
        >
          {title}
        </div>
      )}
      {finished ? (
        <div
          style={{
            fontSize: compact ? 22 : 36,
            fontWeight: 900,
            color: accent,
            letterSpacing: -0.5,
          }}
        >
          {onFinishMessage}
        </div>
      ) : (
        <div
          style={{
            fontSize: compact ? 28 : 44,
            fontWeight: 900,
            fontVariantNumeric: 'tabular-nums',
            color: warning ? '#ef4444' : 'white',
            letterSpacing: -0.5,
            textShadow: warning ? '0 0 16px rgba(239,68,68,0.6)' : undefined,
          }}
        >
          {fmt(seconds, showHours)}
        </div>
      )}
      {!autoStart && (
        <button
          type="button"
          onClick={() => setRunning((r) => !r)}
          style={{
            marginTop: 6,
            background: 'transparent',
            border: `1px solid ${accent}66`,
            color: accent,
            fontSize: 10,
            fontWeight: 700,
            padding: '3px 10px',
            borderRadius: 4,
            letterSpacing: 0.5,
            cursor: 'pointer',
          }}
        >
          {running ? 'PAUSE' : 'START'}
        </button>
      )}
      {pops.map((p) => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            top: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            color: accent,
            fontWeight: 800,
            fontSize: 18,
            animation: 't-pop 1.5s ease-out forwards',
            pointerEvents: 'none',
            textShadow: `0 0 12px ${accent}aa`,
          }}
        >
          {p.text}
        </div>
      ))}
      <style>{`
        @keyframes t-warn { 0%,100%{opacity:1}50%{opacity:0.85} }
        @keyframes t-pop {
          0% { opacity: 0; transform: translate(-50%, 10px); }
          20% { opacity: 1; transform: translate(-50%, 0); }
          80% { opacity: 1; transform: translate(-50%, -20px); }
          100% { opacity: 0; transform: translate(-50%, -40px); }
        }
      `}</style>
    </div>
  );
}
